import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { Table, Tag, Typography, Segmented, Spin, Empty, message } from "antd";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const FINAL_APPROVALS_PATH = "/api/exitprocess/final-approvals/";

const STATUS_COLOR = {
  PENDING: "gold",
  APPROVED: "green",
  REJECTED: "red",
};

function employeeName(emp) {
  if (!emp) return "—";
  return [emp.first_name, emp.last_name].filter(Boolean).join(" ").trim() || emp.emp_code;
}

// Normalizes whatever the API returns into a plain array. Handles:
// - a plain array (non-paginated DRF response)
// - a DRF paginated response: { count, next, previous, results: [...] }
// - anything unexpected (null, error object, etc.) -> []
function normalizeRows(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

export default function FinalApprovalsList() {
  const { authData, backUrl } = useContext(AuthContext);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!authData?.access) return;
    let cancelled = false;
    setLoading(true);

    axios
      .get(`${backUrl}${FINAL_APPROVALS_PATH}`, {
        params: statusFilter === "ALL" ? {} : { status: statusFilter },
        headers: { Authorization: `Bearer ${authData.access}` },
      })
      .then((res) => {
        if (cancelled) return;
        setRows(normalizeRows(res.data));
      })
      .catch((err) => {
        console.error("Error loading final approvals:", err);
        if (!cancelled) setRows([]); // keep Table safe even on failure
        messageApi.error(
          err.response?.status === 403
            ? "You don't have access to final approvals."
            : "Could not load final approvals."
        );
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.access, backUrl, statusFilter]);

  // Navigates to the detail/approve-reject page for a row - only if the
  // row actually has a usable exit_process id, so a malformed row never
  // sends the user to a broken /undefined/final-approval route.
  function goToDetail(row) {
    const exitProcessId = row?.exit_process?.id;
    if (!exitProcessId) {
      messageApi.error("This request is missing exit process details.");
      return;
    }
    navigate(`/exit-process/${exitProcessId}/final-approval`);
  }

  const columns = [
    {
      title: "Employee",
      key: "employee",
      render: (_, row) => employeeName(row.exit_process?.employee),
    },
    {
      title: "Emp Code",
      key: "emp_code",
      render: (_, row) => row.exit_process?.employee?.emp_code || "—",
    },
    {
      title: "Relieving Date",
      key: "relieving_date",
      render: (_, row) =>
        row.exit_process?.relieving_date ? dayjs(row.exit_process.relieving_date).format("DD MMM YYYY") : "—",
    },
    {
      title: "Submitted",
      key: "submitted_at",
      render: (_, row) => (row.submitted_at ? dayjs(row.submitted_at).format("DD MMM YYYY, h:mm A") : "—"),
    },
    {
      title: "Submitted By",
      key: "submitted_by",
      render: (_, row) => employeeName(row.submitted_by),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => <Tag color={STATUS_COLOR[val] || "default"}>{val}</Tag>,
    },
    {
      title: "",
      key: "action",
      render: (_, row) => (
        <Text
          type="link"
          style={{ color: "#1677ff", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            goToDetail(row);
          }}
        >
          View
        </Text>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Title level={4}>Exit Clearance — Final Approvals</Title>

      <Segmented
        options={["PENDING", "APPROVED", "REJECTED", "ALL"]}
        value={statusFilter}
        onChange={setStatusFilter}
        style={{ marginBottom: 16 }}
      />

      <Spin spinning={loading}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          locale={{
            emptyText: (
              <Empty
                description={
                  statusFilter === "PENDING"
                    ? "No exit clearances are waiting for final approval."
                    : "Nothing to show for this filter."
                }
              />
            ),
          }}
          onRow={(row) => ({
            onClick: () => goToDetail(row),
            style: { cursor: "pointer" },
          })}
          pagination={{ pageSize: 10 }}
        />
      </Spin>
    </div>
  );
}